import React, { useEffect, useRef, useState } from "react";
import "./SheetTabs.css";

/**
 * SheetTabs
 * Bottom tab bar — one button per sheet name.  The active sheet is
 * highlighted; clicking another tab calls `onSelect(name)`.
 *
 * The click handler BLURS the tab button: otherwise focus rests on the
 * button and the in-cell editor's keydown handler treated the next
 * keystroke as aimed at a form control, so typing right after switching
 * sheets did nothing (QA #7).
 *
 * When `canRename` is set (edit mode), double-clicking a tab opens an
 * inline rename input (QA #14). Enter/blur commits, Escape cancels.
 */
function SheetTabs({ sheetNames, activeSheet, onSelect, canRename, onRename }) {
  // { name, draft } while a tab is being renamed, else null.
  const [renaming, setRenaming] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);

  const commitRename = () => {
    if (!renaming) return;
    const { name, draft } = renaming;
    setRenaming(null);
    const next = String(draft || "").trim();
    // Excel's sheet-name rules: non-empty, <= 31 chars, no []:*?/\ and
    // no duplicate (case-insensitive).
    if (!next || next === name) return;
    if (next.length > 31) return;
    if (/[[\]:*?/\\]/.test(next)) return;
    if (sheetNames.some((n) => n !== name && n.toLowerCase() === next.toLowerCase())) return;
    if (typeof onRename === "function") onRename(name, next);
  };

  return (
    <div className="simple-excel-sheetbar">
      <div className="simple-excel-tabs" role="tablist" aria-label="Sheets">
        {sheetNames.map((name) => (
          renaming && renaming.name === name ? (
            <input
              key={name}
              ref={inputRef}
              type="text"
              className="simple-excel-tab simple-excel-tab-rename"
              data-allow-text-selection="true"
              value={renaming.draft}
              maxLength={31}
              style={{ width: `${Math.max(6, renaming.draft.length + 2)}ch` }}
              onChange={(e) => setRenaming({ name, draft: e.target.value })}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                else if (e.key === "Escape") setRenaming(null);
                e.stopPropagation();
              }}
            />
          ) : (
            <button
              key={name}
              type="button"
              className={`simple-excel-tab ${activeSheet === name ? "active" : ""}`}
              onClick={(e) => {
                // Return focus to the grid so typing/shortcuts keep working.
                e.currentTarget.blur();
                onSelect(name);
              }}
              onDoubleClick={canRename ? () => setRenaming({ name, draft: name }) : undefined}
              title={canRename ? `${name} — double-click to rename` : name}
            >
              {name}
            </button>
          )
        ))}
      </div>
    </div>
  );
}

export default React.memo(SheetTabs);
